import { PutObjectCommand } from "@aws-sdk/client-s3";
import imageCompression from "browser-image-compression";
import { s3Client, s3Config } from "./s3Config";

/**
 * Compress an image file
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed image file
 */
const compressImage = async (file, options = {}) => {
  const defaultOptions = {
    maxSizeMB: 0.3, // Max file size 300KB
    maxWidthOrHeight: 1280, // Max width or height in pixels
    useWebWorker: true, // Use web worker for better performance
    fileType: file.type, // Preserve original file type
    initialQuality: 0.7, // 70% quality
  };

  const compressionOptions = { ...defaultOptions, ...options };

  try {
    console.log(
      `Original file size: ${(file.size / 1024 / 1024).toFixed(2)} MB`,
    );
    const compressedFile = await imageCompression(file, compressionOptions);
    console.log(
      `Compressed file size: ${(compressedFile.size / 1024 / 1024).toFixed(
        2,
      )} MB`,
    );
    return compressedFile;
  } catch (error) {
    console.error("Error compressing image:", error);
    // Return original file if compression fails
    return file;
  }
};

/**
 * Generate a unique file name for S3 upload
 * @param {File} file - The file to upload
 * @param {string} folder - The folder path in S3 bucket
 * @returns {string} - Unique file name with path
 */
const generateUniqueFileName = (file, folder = "public/tongue") => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  const fileExtension = file.name.split(".").pop();
  const sanitizedFileName = file.name
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-zA-Z0-9]/g, "_");

  return `${folder}/${sanitizedFileName}_${timestamp}_${randomString}.${fileExtension}`;
};

/**
 * Upload a file to S3 with compression
 * @param {File} file - The file to upload
 * @param {string} folder - The folder path in S3 bucket (default: 'public/tongue')
 * @param {Object} compressionOptions - Optional compression options
 * @returns {Promise<{success: boolean, url: string, key: string}>}
 */
export const uploadFileToS3 = async (
  file,
  folder = "public/tongue",
  compressionOptions = {},
) => {
  try {
    // Compress the image before uploading
    const compressedFile = await compressImage(file, compressionOptions);

    const key = generateUniqueFileName(compressedFile, folder);

    // Convert file to ArrayBuffer for upload
    const arrayBuffer = await compressedFile.arrayBuffer();

    const command = new PutObjectCommand({
      Bucket: s3Config.bucketName,
      Key: key,
      Body: arrayBuffer,
      ContentType: compressedFile.type,
      // Make the file publicly readable (adjust based on your needs)
      // ACL: "public-read",
    });

    await s3Client.send(command);

    // Construct the S3 URL
    const url = `https://${s3Config.bucketName}.s3.${s3Config.region}.amazonaws.com/${key}`;

    return {
      success: true,
      url,
      key,
      bucket: s3Config.bucketName,
      originalSize: file.size,
      compressedSize: compressedFile.size,
    };
  } catch (error) {
    console.error("Error uploading file to S3:", error);
    throw new Error(`Failed to upload file: ${error.message}`);
  }
};
